import {
  Card,
  Checkbox,
  CrudMode,
  DataDisplay,
  Grid,
  Stack,
  ValidationErrors,
  formatBoolean,
  pn,
  useElementBreakpoints,
} from "@compass/core";
import { Input, Select } from "components";
import { StudentGroup, StudentGroupEditResource, StudentGroupViewResource } from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useNavigate } from "react-router-dom";
import { studentGroupPageRoute } from "../StudentGroupPage";
import { SelectedStudents } from "./SelectedStudents";

export const StudentGroupPageBody = (props: {
  crudMode: CrudMode;
  group: StudentGroup;
  setGroup: (value: StudentGroup) => void;
  editResource: StudentGroupEditResource;
  viewResource: StudentGroupViewResource;
  validationErrors: ValidationErrors;
  fetch: (id: number) => void;
}) => {
  const navigate = useNavigate();
  const { breakpointsRef, smallFieldBreakpoints } = useElementBreakpoints();
  const properties = props.viewResource.properties || [];

  const nameField = () => {
    const propertyName = pn<StudentGroup>("name");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.group.name}</DataDisplay>
    ) : (
      <Input
        
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.group.name}
        onChange={(e) =>
          props.setGroup(
            produce(props.group, (draft) => {
              draft.name = e.target.value;
            })
          )
        }
      />
    );
  };

  const schoolField = () => {
    const propertyName = pn<StudentGroup>("schoolId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.group.school?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.schools?.find((x) => x.id === props.group.schoolId)}
        options={props.editResource.schools}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setGroup(
            produce(props.group, (draft) => {
              draft.schoolId = value?.id;
            })
          )
        }
      />
    );
  };

  const inactiveField = () => {
    const propertyName = pn<StudentGroup>("inactive");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.group.inactive)}</DataDisplay>
    ) : (
      <Stack height="100%" justifyContent={"center"}>
        <Checkbox
          label={property?.displayName}
          checked={props.group.inactive}
          onChange={(e) =>
            props.setGroup(
              produce(props.group, (draft) => {
                draft.inactive = e.target.checked;
              })
            )
          }
        />
      </Stack>
    );
  };

  return (
    <Stack spacing="lg">
      <Card>
        <Grid ref={breakpointsRef} container spacing="sm">
          <Grid item xs={smallFieldBreakpoints}>
            {nameField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {schoolField()}
          </Grid>

          {props.group.id !== undefined && props.group.id > 0 && (
            <Grid item xs={smallFieldBreakpoints}>
              {inactiveField()}
            </Grid>
          )}
        </Grid>
      </Card>

      <SelectedStudents
        students={props.group.manualStudents || []}
        setStudents={(students) =>
          props.setGroup(
            produce(props.group, (draft) => {
              if (draft) draft.manualStudents = students;
            })
          )
        }
        editResource={props.editResource}
        crudMode={props.crudMode}
        setEditMode={() => {
          navigate({
            pathname: `../${studentGroupPageRoute.getPath({ id: props.group.id!, crudMode: "edit" })}`,
          });
        }}
      />
    </Stack>
  );
};
